import { Component, computed, effect, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ClientUiAuthSerive } from '@roosevelt/client-ui-lib';
import { EligUiAuthSerive } from '@roosevelt/elig-ui-lib';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { getStrictUiAuth } from '../authorizations/selector';
import { IAppAuthorizations, IUiAuthorizations } from '../authorizations/state';
import { getSelectedAdminPlan } from '../login/store/selectors';
import { ILoginState } from '../login/store/state';
import {
  normalizeIsoDate,
  normalizeFoundMemberForPrefill,
  resolveSubscriberName
} from '../shared/member-feed.util';
import { IMassActionState } from '../store/massActions/state';
import { getAddMemberFeed, getSelectedSubscriber } from '../store/member/selector';
import { IMemberState } from '../store/member/state';
import type { AddMemberFeed } from './add-member.component';

type AddMemberContainerLuxState = {
  massAction: IMassActionState;
  member: IMemberState;
  login: ILoginState;
  appUiAuth: IAppAuthorizations;
};

type AddMemberContext = {
  depMemberData: any;
  addMemberFeed: AddMemberFeed | null;
};

@Component({
  selector: 'app-add-member',
  template: `
    <app-add-member-page
      [addFeatures]="addMemberFeatures()"
      [selectedAdminPlan]="selectedAdminPlan()"
      [depMemberData]="addMemberContext().depMemberData"
      [addMemberFeed]="addMemberContext().addMemberFeed"
      (setMemberStore)="setMemberStore($event)"></app-add-member-page>
  `,
  standalone: false
})
export class AddMemberContainer implements OnDestroy {
  readonly luxId = 'BMT-AddMember-component' + this.constructor.name;
  readonly addMemberFeed = toSignal(this.lux.get(getAddMemberFeed, this.luxId), {
    initialValue: null as IMemberState['addMemberFeed'] | null
  });
  readonly selectedSubscriber = toSignal(this.lux.get(getSelectedSubscriber, this.luxId), {
    initialValue: {} as IMemberState['selectedSubscriber']
  });
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly strictAuth = toSignal(this.lux.get(getStrictUiAuth, this.luxId), {
    initialValue: {} as IUiAuthorizations | {}
  });
  readonly addMemberFeatures = computed(() => {
    const uiAuth = this.strictAuth() as IUiAuthorizations;
    return uiAuth?.eligibility?.addMember || [];
  });
  readonly addMemberContext = computed(() => this.buildAddMemberContext(
    this.addMemberFeed(),
    this.selectedSubscriber(),
    this.selectedAdminPlan()
  ));

  constructor(
    private readonly clientUiAuth: ClientUiAuthSerive,
    private readonly eligUiAuth: EligUiAuthSerive,
    private readonly lux: Lux<AddMemberContainerLuxState>
  ) {
    effect(() => {
      const uiAuth = this.strictAuth() as IUiAuthorizations;
      if (uiAuth?.eligibility) {
        this.eligUiAuth.setStrictAuthorizations(uiAuth.eligibility);
      }
      if (uiAuth?.client) {
        this.clientUiAuth.setStrictAuthorizations(uiAuth.client);
      }
    });
  }

  private hasValidAddMemberFeed(feed: IMemberState['addMemberFeed'] | null): feed is IMemberState['addMemberFeed'] {
    return Boolean(feed?.planAcronym && feed?.clientId && feed?.subClientId);
  }

  setMemberStore(storeData: Partial<IMemberState>): void {
    if (storeData) {
      this.lux.set(state => state.member, { ...storeData });
    }
  }

  private buildAddMemberContext(
    feed: IMemberState['addMemberFeed'],
    selectedSubscriber: any = null,
    adminPlan = ''
  ): AddMemberContext {
    if (!this.hasValidAddMemberFeed(feed)) {
      return {
        depMemberData: null,
        addMemberFeed: null
      };
    }

    const depMemberData = this.resolveDepMemberData(feed);
    const subscriberName = feed?.subscriberName
      || resolveSubscriberName(depMemberData)
      || resolveSubscriberName(selectedSubscriber);
    const planAcronym = Array.isArray(feed.planAcronym) ? `${feed.planAcronym[0] ?? ''}` : `${feed.planAcronym ?? ''}`;
    const clientId = this.normalizeInputValue(feed.clientId);
    const subClientId = this.normalizeInputValue(feed.subClientId);
    const memberId = this.normalizeInputValue(feed.memberId);
    const pseudoSsnInd = feed.pseudoSsnInd === true;
    const subscriberEnrollmentID = this.normalizeInputValue(feed.subscriberEnrollmentID);
    const asOf = normalizeIsoDate(feed.asOf) || this.normalizeInputValue(feed.asOf);

    return {
      depMemberData,
      addMemberFeed: {
        clientId,
        subClientId,
        planAcronym,
        memberId,
        pseudoSsnInd,
        subscriberEnrollmentID,
        adminPlan,
        foundMember: depMemberData,
        asOf,
        subscriberName,
        eligibilityEffectiveDate: feed.eligibilityEffectiveDate,
        receivedDate: feed.receivedDate,
        qualifierType: feed.qualifierType,
        memberBenefitType: feed.memberBenefitType,
        eligibilityStatusReason: feed.eligibilityStatusReason,
        eligibilityStatus: feed.eligibilityStatus
      }
    };
  }

  private resolveDepMemberData(feed: IMemberState['addMemberFeed']): any {
    const foundMemberPrefill = normalizeFoundMemberForPrefill(feed?.foundMember) || null;
    if (foundMemberPrefill) {
      return foundMemberPrefill;
    }
    return null;
  }

  private normalizeInputValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return `${value}`.trim();
    }

    return '';
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }
}
